import { t } from "@lingui/macro";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFragment } from "react-relay";

import { useLoyaltyEnrollmentPreference } from "@/features/Loyalty/components/LoyaltyEnrollmentPreference/LoyaltyEnrollmentPreference";
import { PatronCheckoutLoyaltyRewards } from "@/features/Loyalty/components/PatronCheckoutLoyaltyRewards/PatronCheckoutLoyaltyRewards";
import { FacilityApplePayButton } from "@/features/Square/ApplePay";
import { Button } from "@whoosh/design-system/src/components/controls";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useFormatClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { useSlotLockCountdownTimerData } from "@whoosh/utilities/src/hooks";

import { currentProfileAtom } from "../../../features/Club/atoms";
import { useFlags } from "../../../utils/launchDarkly";
import { useDeleteEventSignUp } from "../../EventDetail/hooks/useDeleteEventSignUp";
import { useBuildEventSignUpUpdateInput } from "../hooks/useBuildEventSignUpUpdateInput";
import { useConfirmDraftEventSignUp } from "../hooks/useConfirmDraftEventSignUp";
import { getEventSignupCheckoutState } from "../hooks/useEventSignupCheckoutState";
import { EventSignupPaymentMethodSelect } from "./EventSignupPaymentMethodSelect";
import { EventSignupSummaryTotal } from "./EventSignupSummaryTotal";
import { EventSignupSummaryPaymentSummary_signUp$key } from "./__generated__/EventSignupSummaryPaymentSummary_signUp.graphql";

type Props = {
  fragmentRef: EventSignupSummaryPaymentSummary_signUp$key;
  onExpireCallback: () => void;
  onConfirmSuccess: () => void;
};

export const EventSignupSummaryPaymentSummary = ({
  fragmentRef,
  onExpireCallback,
  onConfirmSuccess,
}: Props) => {
  const formatPrice = useFormatClubCurrency();
  const signUp = useFragment(
    EventSignupSummaryPaymentSummaryFragment,
    fragmentRef
  );
  const currentProfile = useAtomValue(currentProfileAtom);
  const { showToast } = useToast();
  const memberId = currentProfile?.memberId;
  const isMobile = useResponsiveProp({ base: 1, md: 0 });
  const isDraftSignUp = signUp.status === "DRAFT";
  const isWaitlistSignUp = signUp.waitlistIndex !== null;
  const { applePay, eventPrePayment, patronLoyalty } = useFlags();
  const sharedLoyaltyEnrollmentPreference = useLoyaltyEnrollmentPreference();
  const eventPrePaymentEnabled =
    eventPrePayment &&
    (signUp.event?.eventSeries?.facility?.isEventPrePaymentEnabled ?? false);
  const persistedPaymentCardId = signUp.paymentCardOnFile?.id ?? null;
  const [selectedPaymentCardId, setSelectedPaymentCardId] = useState<
    string | null
  >(persistedPaymentCardId);
  const [isPaymentCardUpdatePending, setIsPaymentCardUpdatePending] =
    useState(false);
  const [isApplePayProcessing, setIsApplePayProcessing] = useState(false);
  const [deleteEventSignUp] = useDeleteEventSignUp(onExpireCallback);
  const [confirmDraftEventSignUp, isConfirmingDraftEventSignUp] =
    useConfirmDraftEventSignUp();

  const prePaymentRequirement = signUp.event?.prePaymentRequirement ?? "NONE";
  const prepaymentEnabled =
    eventPrePaymentEnabled &&
    (prePaymentRequirement === "COLLECT_CARD_ON_FILE" ||
      prePaymentRequirement === "FULL");
  const isFullPrepayment =
    eventPrePaymentEnabled && prePaymentRequirement === "FULL";
  const { eventSignUpInput, registrationSubtotal } =
    useBuildEventSignUpUpdateInput(signUp);

  useEffect(() => {
    setSelectedPaymentCardId(persistedPaymentCardId);
  }, [persistedPaymentCardId]);

  const activeOrders =
    signUp.orders?.edges?.flatMap((edge) => (edge?.node ? [edge.node] : [])) ??
    [];
  const { loyaltyOrder, enrollmentOfferOrder, total } =
    getEventSignupCheckoutState({
      orders: activeOrders,
      registrationSubtotal,
      isDraftSignUp,
    });
  const loyaltyRewards = loyaltyOrder?.squareLoyaltyRewards ?? [];
  const effectiveLoyaltyEnrollmentPreference =
    sharedLoyaltyEnrollmentPreference?.preference ?? false;
  const enrollInLoyalty =
    !isWaitlistSignUp &&
    isFullPrepayment &&
    !!patronLoyalty &&
    enrollmentOfferOrder?.loyaltyEnrollmentOffer != null &&
    effectiveLoyaltyEnrollmentPreference;
  const facilityId = signUp.event?.eventSeries?.facility?.id;

  const totalLabel = useMemo(() => {
    if (!eventPrePaymentEnabled) return undefined;
    if (prePaymentRequirement === "COLLECT_CARD_ON_FILE")
      return t`Total due upon arrival`;
    if (prePaymentRequirement === "NONE") return t`Total`;
    return undefined;
  }, [eventPrePaymentEnabled, prePaymentRequirement]);

  const lockedUntil = useMemo(() => {
    const fiveMinutesFromNowMs = Date.now() + 5 * 60 * 1000;
    return new Date(fiveMinutesFromNowMs).toISOString();
  }, []);

  const handleConfirm = useCallback(() => {
    const confirmationInput = {
      eventSignUpId: signUp.id,
      ...(patronLoyalty ? { enrollInLoyalty } : {}),
    };

    confirmDraftEventSignUp(confirmationInput, onConfirmSuccess);
  }, [
    confirmDraftEventSignUp,
    enrollInLoyalty,
    onConfirmSuccess,
    patronLoyalty,
    signUp.id,
  ]);
  const handleApplePayNonce = useCallback(
    (paymentNonce: string) =>
      new Promise<void>((resolve, reject) => {
        confirmDraftEventSignUp(
          {
            eventSignUpId: signUp.id,
            paymentNonce,
            ...(patronLoyalty ? { enrollInLoyalty } : {}),
          },
          resolve,
          reject
        );
      }),
    [confirmDraftEventSignUp, enrollInLoyalty, patronLoyalty, signUp.id]
  );
  const handleApplePayError = useCallback(
    (error: Error) => {
      showToast({
        description: error.message || t`Unable to process Apple Pay.`,
        semanticStyle: "error",
        toastId: "event-apple-pay-error",
      });
    },
    [showToast]
  );
  const handleExpire = useCallback(() => {
    if (!memberId) {
      onExpireCallback();
      return;
    }

    deleteEventSignUp({ eventSignUpId: signUp.id }, memberId);
  }, [deleteEventSignUp, memberId, onExpireCallback, signUp.id]);
  const { timeWarning, timeLow } = useSlotLockCountdownTimerData({
    lockedUntil,
    onExpire: handleExpire,
  });
  const handlePaymentCardUpdatePendingChange = useCallback(
    (isPending: boolean) => {
      setIsPaymentCardUpdatePending(isPending);
    },
    []
  );

  if (!isDraftSignUp) {
    return null;
  }

  // COLLECT_CARD_ON_FILE always requires a card on file, even for a
  // zero-dollar signup -- the backend rejects a missing card unconditionally
  // for that requirement, unlike FULL prepayment, which only needs a card
  // when there's actually a balance to charge.
  const confirmDisabled =
    prepaymentEnabled &&
    (isPaymentCardUpdatePending ||
      (!selectedPaymentCardId &&
        (isWaitlistSignUp || !isFullPrepayment || total > 0)));
  const confirmLabel = isWaitlistSignUp
    ? t`Confirm`
    : isFullPrepayment
    ? t`Confirm and Pay ${formatPrice(total)}`
    : eventPrePaymentEnabled
    ? t`Save and Join`
    : t`Confirm`;

  return (
    <Box>
      <Box
        width={isMobile ? "100%" : 368}
        marginLeft={isMobile ? 0 : 8}
        marginTop={isMobile ? 2 : 8}
        backgroundColor="grayscale-0"
        borderRadius={isMobile ? undefined : "4"}
        overflow="hidden"
        style={isMobile ? undefined : { borderWidth: 1.5 }}
        borderColor="outline-default"
        shadowColor="black"
        shadowOffset={{ width: 0, height: 8 }}
        shadowOpacity={0.06}
        shadowRadius={24}
        testID="event-signup-summary-payment-summary"
      >
        {!isWaitlistSignUp ? (
          <EventSignupSummaryTotal
            fragmentRef={signUp}
            finalAmountLabel={totalLabel}
          />
        ) : null}
        {!isWaitlistSignUp && isFullPrepayment && loyaltyOrder ? (
          <Box paddingHorizontal={5} paddingBottom={5}>
            <PatronCheckoutLoyaltyRewards
              analyticsProperties={{ eventSignUpId: signUp.id }}
              loyaltyRewards={loyaltyRewards}
              orderRef={loyaltyOrder}
              enrollmentOfferRef={patronLoyalty ? enrollmentOfferOrder : null}
              editable={isDraftSignUp}
            />
          </Box>
        ) : null}

        {isDraftSignUp ? (
          <VStack
            padding={5}
            paddingTop={isWaitlistSignUp ? 5 : 0}
            width="100%"
          >
            <VStack gap={3} width="100%">
              {prepaymentEnabled ? (
                <EventSignupPaymentMethodSelect
                  active={true}
                  eventSignUp={eventSignUpInput}
                  eventSignUpId={signUp.id}
                  persistedSelectedCardId={persistedPaymentCardId}
                  selectedCardId={selectedPaymentCardId}
                  setSelectedCardId={setSelectedPaymentCardId}
                  onPaymentCardUpdatePendingChange={
                    handlePaymentCardUpdatePendingChange
                  }
                />
              ) : null}
              {timeWarning ? (
                <HStack justifyContent="center" width="100%">
                  <Text variant="mainSmallSemiBold">{t`Reserve in the next:`}</Text>
                  <Text
                    variant="mainSmallSemiBold"
                    color={timeLow ? "text-error" : "text-main-primary"}
                    testID="event-signup-summary-payment-countdown-timer"
                  >
                    {` ${timeWarning}`}
                  </Text>
                </HStack>
              ) : null}
              {isWaitlistSignUp ? (
                <Text variant="mainSmallMedium" color="text-secondary">
                  {t`You will not be charged automatically; staff will charge you manually if you move off the waitlist.`}
                </Text>
              ) : null}
              <Button
                width="100%"
                isLoading={isConfirmingDraftEventSignUp}
                isDisabled={confirmDisabled}
                onPress={handleConfirm}
                accessibilityLabel={confirmLabel}
                testID="event-signup-summary-confirm-button"
              >
                {confirmLabel}
              </Button>
              {applePay &&
              !isWaitlistSignUp &&
              isFullPrepayment &&
              total > 0 &&
              facilityId ? (
                <FacilityApplePayButton
                  facilityId={facilityId}
                  amount={total}
                  isDisabled={
                    isPaymentCardUpdatePending ||
                    isApplePayProcessing ||
                    isConfirmingDraftEventSignUp
                  }
                  label={t`Whoosh event signup`}
                  onError={handleApplePayError}
                  onNonce={handleApplePayNonce}
                  onProcessingChange={setIsApplePayProcessing}
                  onSuccess={onConfirmSuccess}
                />
              ) : null}
            </VStack>
          </VStack>
        ) : null}
      </Box>
    </Box>
  );
};

export const EventSignupSummaryPaymentSummaryFragment = graphql`
  fragment EventSignupSummaryPaymentSummary_signUp on EventSignUp
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    id
    status
    waitlistIndex
    event {
      id
      prePaymentRequirement
      eventSeries {
        facility {
          id
          isEventPrePaymentEnabled
        }
      }
    }
    paymentCardOnFile {
      id
    }
    ...useBuildEventSignUpUpdateInput_signUp @relay(mask: false)
    orders(active: true) {
      edges {
        node {
          id
          status
          total
          totalTaxes
          availableLoyaltyRewards @include(if: $patronLoyalty) {
            balance
          }
          loyaltyEnrollmentOffer @include(if: $patronLoyalty) {
            points
          }
          orderOwner @include(if: $patronLoyalty) {
            id
            squareLoyaltyAccount {
              id
            }
          }
          squareLoyaltyRewards @include(if: $patronLoyalty) {
            id
            points
            status
            loyaltyCustomerId
            loyaltyAccount {
              id
            }
            rewardTier {
              id
              name
            }
          }
          ...PatronCheckoutLoyaltyRewards_order @include(if: $patronLoyalty)
          ...PatronCheckoutLoyaltyRewardsEnrollmentOffer_order
            @include(if: $patronLoyalty)
        }
      }
    }
    ...EventSignupSummaryTotal_signUp
  }
`;
